import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import StatCard from './components/StatCard';
import { useNavigate } from 'react-router-dom';

interface HomeStats {
  todayServices: number;
  tomorrowServices: number;
  weekServices: number;
  bakimUygun: number;
  bakimZamaniGeldi: number;
  bakimGecikiyor: number;
  kritikGecikme: number;
  monthServices: number;
  todayIncome: number;
  monthIncome: number;
  pendingPayments: number;
  criticalStock: number;
}

interface TodayServiceItem {
  id: string;
  servis_saati: string;
  servis_durumu: string;
  islem_turu: string;
  musteri: { ad: string; soyad: string; telefon: string; acik_adres: string } | null;
  personel: { ad: string; soyad: string } | null;
}

interface BakimItem {
  id: string;
  son_bakim_tarihi: string;
  bir_sonraki_bakim_tarihi: string;
  musteri: { ad: string; soyad: string; telefon: string } | null;
  cihaz: { cihaz_turu: string } | null;
  durumRenk: 'turuncu' | 'kirmizi';
  ayFark: number;
}

const durumRenkMap: Record<string, string> = {
  'Planlandı': 'bg-blue-100 text-blue-700',
  'Servise Çıkıldı': 'bg-orange-100 text-orange-700',
  'Tamamlandı': 'bg-emerald-100 text-emerald-700',
  'İptal Edildi': 'bg-gray-100 text-gray-600',
  'Ertelendi': 'bg-yellow-100 text-yellow-700',
};

export default function HomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<HomeStats>({
    todayServices: 0, tomorrowServices: 0, weekServices: 0,
    bakimUygun: 0, bakimZamaniGeldi: 0, bakimGecikiyor: 0, kritikGecikme: 0,
    monthServices: 0, todayIncome: 0, monthIncome: 0, pendingPayments: 0, criticalStock: 0,
  });
  const [todayServices, setTodayServices] = useState<TodayServiceItem[]>([]);
  const [acilBakimlar, setAcilBakimlar] = useState<BakimItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    async function loadDashboard() {
      setLoading(true);
      setHata(null);
      try {
        const today = new Date().toISOString().split('T')[0];
        const tomorrowDate = new Date(); tomorrowDate.setDate(tomorrowDate.getDate() + 1);
        const tomorrow = tomorrowDate.toISOString().split('T')[0];
        const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7);
        const weekEndStr = weekEnd.toISOString().split('T')[0];
        const monthStart = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
        const monthEnd = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-31`;

        const [
          { count: todayCount }, { count: tomCount }, { count: weekCount },
          { count: monthCount }, { data: todayPayments }, { data: monthPayments },
          { count: pendingCount }, { count: stockCount },
        ] = await Promise.all([
          supabase.from('sadem_servis_kayitlari').select('*', { count: 'exact', head: true }).eq('servis_tarihi', today).in('servis_durumu', ['Planlandı', 'Servise Çıkıldı', 'planlandi', 'yolda']),
          supabase.from('sadem_servis_kayitlari').select('*', { count: 'exact', head: true }).eq('servis_tarihi', tomorrow).in('servis_durumu', ['Planlandı', 'Servise Çıkıldı', 'planlandi', 'yolda']),
          supabase.from('sadem_servis_kayitlari').select('*', { count: 'exact', head: true }).gte('servis_tarihi', today).lte('servis_tarihi', weekEndStr).in('servis_durumu', ['Planlandı', 'Servise Çıkıldı', 'planlandi', 'yolda']),
          supabase.from('sadem_servis_kayitlari').select('*', { count: 'exact', head: true }).gte('servis_tarihi', monthStart).lte('servis_tarihi', monthEnd).eq('servis_durumu', 'Tamamlandı'),
          supabase.from('sadem_odemeler').select('tutar').eq('odeme_tarihi', today).in('odeme_durumu', ['Ödendi', 'Kısmi Ödendi', 'odendi', 'kismi']),
          supabase.from('sadem_odemeler').select('tutar').gte('odeme_tarihi', monthStart).lte('odeme_tarihi', monthEnd).in('odeme_durumu', ['Ödendi', 'Kısmi Ödendi', 'odendi', 'kismi']),
          supabase.from('sadem_odemeler').select('*', { count: 'exact', head: true }).eq('odeme_durumu', 'Ödeme Bekliyor'),
          supabase.from('sadem_stok_urunleri').select('*', { count: 'exact', head: true }).lt('stok_miktari', 1).eq('aktif', true),
        ]);

        const todayIncome = (todayPayments || []).reduce((s, p) => s + Number(p.tutar || 0), 0);
        const monthIncome = (monthPayments || []).reduce((s, p) => s + Number(p.tutar || 0), 0);

        setStats(prev => ({
          ...prev,
          todayServices: todayCount || 0,
          tomorrowServices: tomCount || 0,
          weekServices: weekCount || 0,
          monthServices: monthCount || 0,
          todayIncome,
          monthIncome,
          pendingPayments: pendingCount || 0,
          criticalStock: stockCount || 0,
        }));

        // Bugünkü servis listesi
        const { data: svData } = await supabase
          .from('sadem_servis_kayitlari')
          .select('id, servis_saati, servis_durumu, islem_turu, musteri:sadem_musteriler(ad, soyad, telefon, acik_adres), personel:sadem_personeller(ad, soyad)')
          .eq('servis_tarihi', today)
          .in('servis_durumu', ['Planlandı', 'Servise Çıkıldı', 'planlandi', 'yolda'])
          .order('servis_saati', { ascending: true })
          .limit(20);

        if (svData) setTodayServices(svData as unknown as TodayServiceItem[]);

        // Firma ayarlarını çek
        const { data: ayarlar } = await supabase.from('sadem_firma_ayarlari').select('sari_baslangic_ay, turuncu_baslangic_ay, kirmizi_baslangic_ay').maybeSingle();
        const sariAy = ayarlar?.sari_baslangic_ay ?? 6;
        const turuncuAy = ayarlar?.turuncu_baslangic_ay ?? 9;
        const kirmiziAy = ayarlar?.kirmizi_baslangic_ay ?? 12;

        // Bakım verileri
        const { data: bakimData } = await supabase
          .from('sadem_bakim_takibi')
          .select('id, son_bakim_tarihi, bir_sonraki_bakim_tarihi, musteri:sadem_musteriler(ad, soyad, telefon), cihaz:sadem_cihazlar(cihaz_turu)')
          .eq('aktif', true)
          .order('son_bakim_tarihi', { ascending: true })
          .limit(200);

        if (bakimData) {
          const now = new Date();
          let uygun = 0, zamaniGeldi = 0, gecikiyor = 0, kritik = 0;
          const kritikList: BakimItem[] = [];
          const turuncuList: BakimItem[] = [];

          for (const b of bakimData as unknown as any[]) {
            const sonBakim = b.son_bakim_tarihi ? new Date(b.son_bakim_tarihi) : null;
            if (!sonBakim) continue;

            const sariTarih = new Date(sonBakim); sariTarih.setMonth(sariTarih.getMonth() + sariAy);
            const turuncuTarih = new Date(sonBakim); turuncuTarih.setMonth(turuncuTarih.getMonth() + turuncuAy);
            const kirmiziTarih = new Date(sonBakim); kirmiziTarih.setMonth(kirmiziTarih.getMonth() + kirmiziAy);
            const ayFark = (now.getFullYear() - sonBakim.getFullYear()) * 12 + (now.getMonth() - sonBakim.getMonth());

            if (now >= kirmiziTarih) {
              kritik++;
              kritikList.push({ ...b, durumRenk: 'kirmizi', ayFark });
            } else if (now >= turuncuTarih) {
              gecikiyor++;
              turuncuList.push({ ...b, durumRenk: 'turuncu', ayFark });
            } else if (now >= sariTarih) {
              zamaniGeldi++;
            } else {
              uygun++;
            }
          }

          // Önce kırmızı, sonra turuncu
          setAcilBakimlar([...kritikList.slice(0, 5), ...turuncuList.slice(0, 3)]);
          setStats(prev => ({ ...prev, bakimUygun: uygun, bakimZamaniGeldi: zamaniGeldi, bakimGecikiyor: gecikiyor, kritikGecikme: kritik }));
        }
      } catch (err) {
        setHata('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyiniz.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, []);

  const formatPara = (v: number) =>
    v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-3 text-gray-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
          <span className="text-sm">Panel verileri yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (hata) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-md">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <p className="text-sm text-red-700 font-medium">{hata}</p>
          <button onClick={() => window.location.reload()}
            className="mt-4 text-xs bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 cursor-pointer">Yeniden Dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Hoş geldiniz!</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          {new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} tarihli servis özeti
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard title="Bugünkü Servisler" value={stats.todayServices} icon="ri-tools-line" iconBg="bg-sky-100" iconColor="text-sky-600" subtitle="bugün planlı" />
        <StatCard title="Yarınki Servisler" value={stats.tomorrowServices} icon="ri-calendar-2-line" iconBg="bg-orange-100" iconColor="text-orange-500" subtitle="yarın planlı" />
        <StatCard title="Bu Hafta Planlanan" value={stats.weekServices} icon="ri-calendar-check-line" iconBg="bg-blue-100" iconColor="text-blue-600" subtitle="bu hafta" />
        <StatCard title="Bakım Süresi Uygun" value={stats.bakimUygun} icon="ri-checkbox-circle-line" iconBg="bg-emerald-100" iconColor="text-emerald-600" subtitle="bakım süresi uygun" />
        <StatCard title="Bakım Zamanı Gelenler" value={stats.bakimZamaniGeldi} icon="ri-time-line" iconBg="bg-yellow-100" iconColor="text-yellow-600" subtitle="bakım zamanı geldi" />
        <StatCard title="Bakımı Gecikenler" value={stats.bakimGecikiyor} icon="ri-alert-line" iconBg="bg-orange-100" iconColor="text-orange-600" subtitle="bakım gecikiyor" />
        <StatCard title="Kritik Gecikmeler" value={stats.kritikGecikme} icon="ri-alarm-warning-line" iconBg="bg-red-100" iconColor="text-red-500" subtitle="kritik bakım gecikmesi" />
        <StatCard title="Bu Ay Yapılan" value={stats.monthServices} icon="ri-check-double-line" iconBg="bg-emerald-100" iconColor="text-emerald-600" subtitle="tamamlanan servis" />
        <StatCard title="Bugünkü Tahsilat" value={formatPara(stats.todayIncome)} icon="ri-money-dollar-circle-line" iconBg="bg-green-100" iconColor="text-green-600" subtitle="bugün toplam" />
        <StatCard title="Bu Aylık Tahsilat" value={formatPara(stats.monthIncome)} icon="ri-line-chart-line" iconBg="bg-violet-100" iconColor="text-violet-600" subtitle="bu ay toplam" />
        <StatCard title="Bekleyen Ödemeler" value={stats.pendingPayments} icon="ri-time-line" iconBg="bg-orange-100" iconColor="text-orange-600" subtitle="ödeme bekliyor" />
        <StatCard title="Kritik Stok" value={stats.criticalStock} icon="ri-store-2-line" iconBg="bg-red-100" iconColor="text-red-600" subtitle="kritik seviye" />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800">Bugün Gidilecek Servisler</h2>
              <span className="bg-sky-100 text-sky-700 text-xs font-semibold px-2.5 py-1 rounded-full">{todayServices.length} servis</span>
            </div>
            {todayServices.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">Bu tarihte planlanmış servis bulunmuyor.</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {todayServices.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-slate-50 transition-colors">
                    <div className="bg-sky-600 text-white text-xs font-bold px-2.5 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0">
                      {s.servis_saati || '--:--'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {s.musteri ? `${s.musteri.ad} ${s.musteri.soyad}` : 'Bilinmiyor'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{s.musteri?.acik_adres || ''}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium hidden md:block whitespace-nowrap flex-shrink-0 ${durumRenkMap[s.servis_durumu] || 'bg-gray-100 text-gray-600'}`}>
                      {s.servis_durumu}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap hidden sm:block flex-shrink-0">
                      {s.islem_turu}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a href={`tel:${s.musteri?.telefon || ''}`}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-green-50 text-green-600 hover:bg-green-100 cursor-pointer" title="Ara">
                        <i className="ri-phone-line text-sm"></i>
                      </a>
                      <button onClick={() => navigate('/servis-kayitlari')}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100 cursor-pointer" title="Detay">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Acil Bakım */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-red-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-red-100">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-alert-line text-red-500"></i>
                </div>
                <h2 className="text-base font-bold text-gray-800">Acil Bakım Gerekenler</h2>
              </div>
              <span className="bg-red-100 text-red-600 text-xs font-semibold px-2.5 py-1 rounded-full">{acilBakimlar.length} kişi</span>
            </div>
            {acilBakimlar.length === 0 ? (
              <div className="px-5 py-6 text-center text-sm text-gray-400">Bakımı geciken müşteri bulunmuyor.</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {acilBakimlar.map((b) => (
                  <div key={b.id} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{b.musteri ? `${b.musteri.ad} ${b.musteri.soyad}` : 'Bilinmiyor'}</p>
                        <p className="text-xs text-gray-400">{b.musteri?.telefon || ''}</p>
                        <p className="text-xs text-gray-400">{b.cihaz?.cihaz_turu || ''}</p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap flex-shrink-0 ${b.durumRenk === 'kirmizi' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                        {b.ayFark} ay
                      </span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <a href={`tel:${b.musteri?.telefon || ''}`}
                        className="flex-1 flex items-center justify-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-1.5 rounded-lg transition-colors cursor-pointer">
                        <i className="ri-phone-line"></i><span>Ara</span>
                      </a>
                      <button onClick={() => navigate('/mesaj-gonder')}
                        className="flex-1 flex items-center justify-center gap-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 font-medium py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-whatsapp-line"></i><span>Mesaj</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="px-5 py-3 border-t border-red-100">
              <button onClick={() => navigate('/bakim-takip')}
                className="w-full text-xs text-red-600 font-medium hover:text-red-800 cursor-pointer text-center">
                Tümünü Görüntüle →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hızlı İşlemler */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-4">Hızlı İşlemler</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { icon: 'ri-user-add-line', label: 'Yeni Müşteri Ekle', path: '/musteriler', color: 'text-sky-600 bg-sky-50 hover:bg-sky-100' },
            { icon: 'ri-add-circle-line', label: 'Yeni Servis Kaydı', path: '/servis-kayitlari', color: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' },
            { icon: 'ri-calendar-check-line', label: 'Servis Planla', path: '/servis-planlama', color: 'text-orange-600 bg-orange-50 hover:bg-orange-100' },
            { icon: 'ri-shield-check-line', label: 'Bakım Listesini Aç', path: '/bakim-takip', color: 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100' },
            { icon: 'ri-message-3-line', label: 'Mesaj Şablonları', path: '/mesaj-gonder', color: 'text-violet-600 bg-violet-50 hover:bg-violet-100' },
            { icon: 'ri-printer-line', label: 'Günlük Servis Listesi', path: '/servis-planlama', color: 'text-gray-600 bg-gray-50 hover:bg-gray-100' },
          ].map((action) => (
            <a key={action.path + action.label} href={action.path}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl font-medium text-sm transition-colors cursor-pointer ${action.color}`}>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${action.icon} text-2xl`}></i>
              </div>
              <span className="text-center text-xs">{action.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}